/**
 * Header → NewProjectForm mapping for the pre-import "create new project" flow.
 *
 * The preflight dialog receives a PreflightHeaderMetadata (insured_name /
 * property_address / type_of_loss), which differs from the post-parse
 * EstimateHeaderMetadata (owner_name / address / loss_type). This centralizes the
 * field selection + trimming rules and intentionally MIRRORS the post-parse
 * create logic (CreateProjectForm.handleSubmit: send only populated, trimmed core
 * fields) so a project created from the preflight header matches one created from
 * the post-parse header.
 */
import type { NewProjectForm, PreflightHeaderMetadata } from '../../types';

/** Trim a nullable string to a non-empty value, or undefined. */
function clean(v: string | null | undefined): string | undefined {
  const t = v?.trim();
  return t ? t : undefined;
}

/**
 * Build a NewProjectForm from the FREE preflight header (PreflightHeaderMetadata).
 *
 * Mirrors the post-parse mapping (CreateProjectForm.handleSubmit): address goes
 * to street/city/state/zip and only populated, trimmed fields are included.
 * insured_name → owner_name and type_of_loss → loss_type bridge the preflight
 * naming to the canonical NewProjectForm fields.
 */
export function preflightHeaderToNewProjectForm(
  header: PreflightHeaderMetadata,
): NewProjectForm {
  const addr = header.property_address;
  const form: NewProjectForm = {
    street: clean(addr?.street) ?? '',
    city: clean(addr?.city) ?? '',
    state: clean(addr?.state) ?? '',
    zip: clean(addr?.zip) ?? '',
  };
  const owner = clean(header.insured_name);
  const phone = clean(header.owner_phone);
  const claim = clean(header.claim_number);
  const policy = clean(header.policy_number);
  const loss = clean(header.type_of_loss);
  const dol = clean(header.date_of_loss);
  if (owner) form.owner_name = owner;
  if (phone) form.owner_phone = phone;
  if (claim) form.claim_number = claim;
  if (policy) form.policy_number = policy;
  if (loss) form.loss_type = loss;
  if (dol) form.date_of_loss = dol;
  return form;
}
